import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import { Loader2 } from 'lucide-react'
import api from '@/lib/api/axios'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface ScanResultEntry {
    objectID: string
    objectName?: string
    datetime?: string
    analytics: number
    description?: string
    vaultName?: string
}

interface ScanResultsData {
    summary: { green: number; amber: number; red: number }
    threats: ScanResultEntry[]
    informational: ScanResultEntry[]
}

export function ScanResultsTab() {
    const { t } = useTranslation()

    const { data, isLoading } = useQuery({
        queryKey: ['analytics', 'scan-results'],
        queryFn: async () => {
            const res = await api.get('/api/analytics/scan-results')
            return (res.data?.data || res.data || {}) as ScanResultsData
        },
    })

    const pieOption = useMemo((): EChartsOption => {
        const s = data?.summary
        if (!s) return {}

        return {
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)',
            },
            legend: {
                bottom: 0,
                textStyle: { fontSize: 12, color: '#888' },
            },
            series: [
                {
                    type: 'pie',
                    radius: ['40%', '70%'],
                    center: ['50%', '45%'],
                    avoidLabelOverlap: true,
                    itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
                    label: { show: true, formatter: '{b}\n{c}' },
                    data: [
                        { value: s.green, name: 'Green (4)', itemStyle: { color: '#22c55e' } },
                        { value: s.amber, name: 'Amber (3)', itemStyle: { color: '#f59e0b' } },
                        { value: s.red, name: 'Red (2)', itemStyle: { color: '#ef4444' } },
                    ],
                },
            ],
        }
    }, [data])

    const threats = data?.threats || []
    const informational = data?.informational || []

    return (
        <Card>
            <CardHeader>
                <CardTitle>{t('analytics.scanResults', 'Analytics Scan Results')}</CardTitle>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Loading scan results...
                    </div>
                ) : !data?.summary ? (
                    <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
                        No scan results data available.
                    </div>
                ) : (
                    <>
                        <ReactECharts option={pieOption} style={{ height: 320 }} opts={{ renderer: 'canvas' }} />

                        {/* Threats table (analytics=2) */}
                        {threats.length > 0 && (
                            <div className="mt-6">
                                <h3 className="text-sm font-semibold mb-2 text-red-600">Threats (analytics = 2)</h3>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b text-left text-muted-foreground">
                                                <th className="pb-2 pr-4 font-medium">Object</th>
                                                <th className="pb-2 pr-4 font-medium">Date</th>
                                                <th className="pb-2 pr-4 font-medium">Description</th>
                                                <th className="pb-2 font-medium">Vault</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {threats.map((e, i) => (
                                                <tr key={i} className="border-b last:border-0">
                                                    <td className="py-2 pr-4 font-mono text-xs">{e.objectID}</td>
                                                    <td className="py-2 pr-4 whitespace-nowrap">
                                                        {e.datetime ? new Date(e.datetime).toLocaleDateString() : '—'}
                                                    </td>
                                                    <td className="py-2 pr-4 max-w-[300px] truncate">{e.description || '—'}</td>
                                                    <td className="py-2">{e.vaultName || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Informational table (analytics=3) */}
                        {informational.length > 0 && (
                            <div className="mt-6">
                                <h3 className="text-sm font-semibold mb-2 text-amber-600">Informational (analytics = 3)</h3>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b text-left text-muted-foreground">
                                                <th className="pb-2 pr-4 font-medium">Object</th>
                                                <th className="pb-2 pr-4 font-medium">Date</th>
                                                <th className="pb-2 pr-4 font-medium">Description</th>
                                                <th className="pb-2 font-medium">Vault</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {informational.map((e, i) => (
                                                <tr key={i} className="border-b last:border-0">
                                                    <td className="py-2 pr-4 font-mono text-xs">{e.objectID}</td>
                                                    <td className="py-2 pr-4 whitespace-nowrap">
                                                        {e.datetime ? new Date(e.datetime).toLocaleDateString() : '—'}
                                                    </td>
                                                    <td className="py-2 pr-4 max-w-[300px] truncate">{e.description || '—'}</td>
                                                    <td className="py-2">{e.vaultName || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    )
}
